import React from "react";
import { isNullOrUndefined } from "util";
import {
    SEVERITY_ID,
    SUMMARY_ID,
    DESCRIPTION_ID,
    START_ID,
    END_ID,
    DATE_SUFFIX,
    TIME_SUFFIX,
    COMPOSITE_FIELD_VALUE_SEP,
    DIV_TAG_NAME,
    EMPTY_STRING,
    allowlistedFormElements
} from "src/common/constants/formConstants";
import { getUTCDateTime, getEpoch } from "src/common/utils/dateUtils";
import { IEvent } from "src/context/models/Event";;

/**
 * Sets state of all elements to "error" that have no value and
 * returns false if at least one element has no value
 *
 * @param React.RefObject<any>[] elementRefs
 * @returns boolean
 **/
export function isElementsFromRefsValid(elementRefs: React.RefObject<any>[]): boolean {
    let isValid = true;
    if (!isNullOrUndefined(elementRefs) && elementRefs.length != 0) {
        elementRefs.forEach((elementRef: React.RefObject<any>) => {
            isValid = isElementFromRefValid(elementRef) && isValid;
        });
    }
    return isValid;
}

/**
 * Sets state of the element to "error" and returns false if the element has no value and
 *
 * @param React.RefObject<any> elementRef
 * @returns boolean
 **/
function isElementFromRefValid(elementRef: React.RefObject<any>): boolean {
    let isValid = true;
    if (!isRefNull(elementRef)) {
        const element = document.getElementById(elementRef.current.id);
        if (!isNullOrUndefined(element)) {
            getChildElements(element)
                .forEach((childElement: any) => {
                    isValid = hasValue(childElement) && isValid;
                    //@ts-ignore
                    childElement.state = isValid ? "" : "error";
                });
        }
    }
    return isValid;
}

/**
 * Sets state of the elements to "error" and return false if the start date is greater than the end date
 *
 * @param React.RefObject<HTMLDivElement> startDateTimeElementRef
 * @param React.RefObject<HTMLDivElement> endDateTimeElementRef
 * @returns boolean
 **/
export function isDateRangeFromRefsValid(
        startDateTimeElementRef: React.RefObject<HTMLDivElement>, endDateTimeElementRef: React.RefObject<HTMLDivElement>
) : boolean {
    const startDateTime = getElementDataFromRef(startDateTimeElementRef).value;
    const endDateTime = getElementDataFromRef(endDateTimeElementRef).value;
    if (!isRefNull(startDateTimeElementRef)
        && !isRefNull(endDateTimeElementRef)
        && !isNullOrUndefined(startDateTime)
        && !isNullOrUndefined(endDateTime)
        && getEpoch(startDateTime) > getEpoch(endDateTime)) {
            [startDateTimeElementRef?.current?.id, endDateTimeElementRef?.current?.id]
                .forEach((id: string | undefined) => {
                    const element = !isNullOrUndefined(id) ? document.getElementById(id) : null;
                    getChildElements(element)
                        .forEach((childElement: any) => {
                            //@ts-ignore
                            childElement.state = "error";
                        });
                });
            return false;
    }
    return true;
}

/**
 * Extracts data from element refs
 *
 * @param React.RefObject<any>[] elementRefs
 * @returns {[key: string]: string}
 **/
export function getElementsDataFromRefs(elementRefs: React.RefObject<any>[]): {[key: string]: string} {
    const formData: {[key: string]: string} = {};
    if (!isNullOrUndefined(elementRefs) && elementRefs.length != 0) {
        elementRefs.forEach((elementRef: React.RefObject<any>) => {
            const {key, value} = getElementDataFromRef(elementRef);
            if (!isNullOrUndefined(key) && !isNullOrUndefined(value)) {
                //@ts-ignore
                formData[key] = value;
            }
        });
    }
    return formData;
}

/**
 * Extracts data from an element ref
 *
 * @param React.RefObject<any> elementRef
 * @returns {key: string, value | null: string}
 **/
export function getElementDataFromRef(elementRef: React.RefObject<any>): {key: string, value: string | null} {
    let key = null;
    let value: string | null = null;
    if (!isRefNull(elementRef)) {
        const element = document.getElementById(elementRef.current.id);
        if (!isNullOrUndefined(element)) {
            key = elementRef.current.id;
            getChildElements(element)
                .forEach((childElement: any) => {
                    //@ts-ignore
                    value = isNullOrUndefined(value) ?
                        childElement.value :
                        value + COMPOSITE_FIELD_VALUE_SEP + childElement.value;
                });
        }
    }
    return {key, value}
}

/**
 * A single element can represent a simple form element or
 * a composite form element which consists of other elements enclosed within a div (eg. date time picker)
 *
 * @param any element
 * @returns [] array of single or multiple elements
 **/
function getChildElements(element: any): any[] {
    const elements = [];
    if (DIV_TAG_NAME === element.tagName) {
        // Use children of the div element to extract the form elements
        const filteredChildren = Array.from(element.children)
            .filter((child: any) => allowlistedFormElements.includes(child.tagName))
        elements.push(...filteredChildren)
    } else {
        elements.push(element)
    }
    return elements;
}

function hasValue(element: any): boolean {
    //@ts-ignore
    return !isNullOrUndefined(element.value) && element.value.trim() !== EMPTY_STRING;
}

export function resetFormData(formRef: React.RefObject<HTMLFormElement>) {
    if (!isNullOrUndefined(formRef)
            && !isNullOrUndefined(formRef.current)) {
        formRef.current.reset();
    }
}

export function isRefNull(ref: React.RefObject<any>) {
    return isNullOrUndefined(ref)
        || isNullOrUndefined(ref.current)
        || isNullOrUndefined(ref.current.id)
        || ref.current.id === "";
}

export function constructFormData(event: IEvent): {[key: string]: string | undefined } {
    return {
        [SEVERITY_ID]: event.eventSeverity,
        [SUMMARY_ID]: event.summary,
        [DESCRIPTION_ID]: event.description,
        [START_ID]: getUTCDateTime(event.start),
        [END_ID]: getUTCDateTime(event.end)
    };
}

export function constructEvent(formData: {[key: string]: string}, region: string, type: string,
        section: string, operation: string): IEvent {
    return {
        eventSeverity: formData[SEVERITY_ID],
        summary: formData[SUMMARY_ID],
        description: formData[DESCRIPTION_ID],
        start: getEpoch(formData[START_ID]),
        end: formData[END_ID] != undefined ? getEpoch(formData[END_ID]) : null,
        region,
        type,
        section,
        operation
    };
}